import { DEFAULT_DECIMAL_DISPLAY_POSITION } from 'app-constants'
import Big from 'big.js'
import moment from 'moment'
import { ProductOverview } from 'pages/overview/storage/interface'
import { assertMultiply } from 'utils'

type performanceKey = 'lastTwoDays' | 'lastWeek' | 'lastThreeMonths' | 'lastYear' | 'inception'
interface PerformanceStat {
  key: performanceKey
  value: string
  label: string
  date: number
}

/**
 * Returns performance over time
 * @param targetPrice Typically the Index.basePrice
 * @returns Array @see PerformanceStat
 */
export const getPerformanceOverTime = (indexData: ProductOverview): PerformanceStat[] => {
  // error boundary
  if (!indexData) {
    throw new Error('Please provide an index for performance over time statistics')
  }
  const { priceHistory } = indexData

  const ONE_YEAR = 31536000
  const NOW = moment().unix()
  const WEEK = 604800
  const DAY = 86400 * 2
  const YEAR_BEFORE = Math.floor(NOW - ONE_YEAR)
  const THREE_MONTHS_BEFORE = Math.floor(NOW - ONE_YEAR / 4)
  const WEEK_BEFORE = Math.floor(NOW - WEEK)
  const DAY_BEFORE = Math.floor(NOW - DAY)

  const performanceInterval: { key: performanceKey; label: string; date: number }[] = [
    { key: 'lastTwoDays', label: '2 Days', date: DAY_BEFORE },
    { key: 'lastWeek', label: '7 Days', date: WEEK_BEFORE },
    { key: 'lastThreeMonths', label: '3 Months', date: THREE_MONTHS_BEFORE },
    { key: 'lastYear', label: '1 Year', date: YEAR_BEFORE },
    { key: 'inception', label: 'Inception', date: NOW },
  ]

  const sortedDailyStats = priceHistory.sort((a, b) => {
    if (a.date < b.date) return -1
    if (a.date > b.date) return 1
    return 0
  })

  const percentageDelta = (startPrice: Big, endPrice: Big): string => {
    const delta = endPrice.minus(startPrice)
    assertMultiply(delta.div(startPrice), 100)
    const value = delta.div(startPrice).mul(100).toFixedSpecial(DEFAULT_DECIMAL_DISPLAY_POSITION)
    return value
  }

  return performanceInterval.reduce<PerformanceStat[]>((acc, intervalData) => {
    const result = sortedDailyStats.filter((el) => moment(el.date).unix() > intervalData.date)

    if (result.length > 0) {
      const startIntervalDate = result[0]
      const finishIntervalDate = result[result.length - 1]
      if (startIntervalDate.basePrice && finishIntervalDate.basePrice) {
        acc.push({
          ...intervalData,
          value: percentageDelta(startIntervalDate.basePrice, finishIntervalDate.basePrice),
        })
      } else {
        console.error(
          `basePrice missing dailyStat: ${startIntervalDate.basePrice}, index: ${finishIntervalDate.basePrice}`,
        )
      }
    }
    return acc
  }, [])
}
